/**
 * Shared pure-data types used by both the PartyServer (party/) and the
 * client (src/).
 *
 * IMPORTANT: This file must NOT import from any browser-only package
 * (e.g. `ai`, `@tiptap/react`, `react`, `dexie`). It is compiled by both
 * the Wrangler build (party/) and the Vite build (src/).
 *
 * When adding types here, ensure they are plain data shapes — no class
 * instances, no framework-specific types, no side-effects.
 */
import type { StoredQuizQuestion } from '../../quiz/logic';

// ---------------------------------------------------------------------------
// Branded types — prevent mixing up deck IDs and deck paths at compile time
// ---------------------------------------------------------------------------

/** UUID primary key for a deck. Use for Zustand keys, sync protocol, DO storage, URLs. */
export type DeckId = string & { readonly __brand: 'DeckId' };

/** Legacy path name for a deck. Use for deckSource/deckPdf lookups, notes, YJS rooms, Supabase API calls. */
export type DeckPath = string & { readonly __brand: 'DeckPath' };

/** Cast a string to DeckId (use at creation/migration boundaries). */
export const asDeckId = (s: string): DeckId => s as DeckId;

/** Cast a string to DeckPath (use at creation/migration boundaries). */
export const asDeckPath = (s: string): DeckPath => s as DeckPath;

// ---------------------------------------------------------------------------
// Card study / spaced repetition
// ---------------------------------------------------------------------------

export const cardStudyActionTypes = ['bad', 'hard', 'good', 'easy'] as const;
export type CardStudyActionType = (typeof cardStudyActionTypes)[number];

export type CardStudyPhase = 'learn' | 'review' | 'lapsed';

/**
 * Duration in either minutes or days (mutually exclusive).
 * Both fields are accessible (as `number | undefined`) so code can use
 * truthiness checks like `if (duration.days)` without narrowing first.
 */
export type AnkiDuration =
  | { minutes: number; days?: undefined }
  | { days: number; minutes?: undefined };

export type SRAnkiAlgoInfo = {
  type: 'anki';
  ease: number;
  nextDueISO: string;
  interval: AnkiDuration;
  studyPhase: CardStudyPhase;
};

export type SRLegacyAlgoInfo = {
  type: 'legacy';
  nextDueISO: string;
  interval: AnkiDuration;
  studyPhase: CardStudyPhase;
};

export type SRAlgoInfo = SRAnkiAlgoInfo | SRLegacyAlgoInfo;

export type CardStudyState = {
  type: CardStudyActionType;
  ISO: string;
  algoInfo: SRAlgoInfo;
};

// ---------------------------------------------------------------------------
// Re-exports from entities (entity types defined via Zod there)
// ---------------------------------------------------------------------------

export type { SpacedRepetitionSettings, SyncFolder, SyncUserInfo } from './entities';

// ---------------------------------------------------------------------------
// Spaced repetition settings
// ---------------------------------------------------------------------------

export type SpacedRepetitionConfig = {
  newCardsPerDay: number;
  reviewsPerDay: number;
  graduationStepsMinutes: number[];
  relearningStepsMinutes: number[];
  startingEase: number;
  graduationIntervalDays: number;
  minimumIntervalDays: number;
  minEase: number;
  maxEase: number;
  badEaseChange: number;
  hardEaseChange: number;
  hardLateMultiplier: number;
  hardFixedInterval: number;
  goodEaseChange: number;
  goodLateMultiplier: number;
  easyEaseChange: number;
  easyBonus: number;
  easyLateMultiplier: number;
  easyIntervalDays: number;
  fuzzFactor: number;
  maxIntervalDays: number;
  reviewIntervalModifier: number;
  leechThreshold: number;
  leechAction: 'disabled' | 'tag' | 'suspend';
  cutoffTime: { hours: number; minutes: number };
};

// SpacedRepetitionSettings is now defined via Zod in sync-schema.ts
// and re-exported above. SpacedRepetitionConfig stays here as it's
// not a sync entity — it's consumed by the SR algorithm.

// ---------------------------------------------------------------------------
// Card types
// ---------------------------------------------------------------------------

export type ISOObject = { ISO: string };

/**
 * Card action descriptor — exactly one key with an ISO timestamp value.
 * Mirrors `ExactlyOneKey<Record<"good" | "bad" | "delete", ISOObject>>`.
 */
export type CardAction =
  | { good: ISOObject; bad?: never; delete?: never }
  | { bad: ISOObject; good?: never; delete?: never }
  | { delete: ISOObject; good?: never; bad?: never };

export type AINotesTypes = 'mnemonic' | 'explanation' | 'visualize' | 'example' | 'chat';

export type ImageFormat = { width: string; height: string };

export type CardTag = {
  type: 'leech';
  timeAddedISO: string;
};

/**
 * Chat message shape — structurally compatible with Vercel AI SDK `Message`.
 * Defined here without importing the `ai` package so party/ can use it.
 *
 * NOTE: `createdAt` is typed as `Date` to match the AI SDK. At runtime,
 * JSON-serialized messages will have `createdAt` as a string — this is fine
 * because the sync layer and IndexedDB just pass the value through without
 * inspecting it. The AI SDK's `useChat` handles the Date/string conversion
 * internally.
 */
export type SyncChatMessage = {
  id: string;
  role: 'system' | 'user' | 'assistant' | 'data';
  content: string;
  createdAt?: Date;
};

/**
 * Core card data shape — the serializable subset of CardProps.
 *
 * This is the single source of truth for the card shape used across
 * both the client and the sync server. The frontend's `CardProps` in
 * `src/lib/decks-utils/deck-types.ts` should be a superset of this
 * (adding the `Message` type from `ai` for `chat`).
 */
export type SyncCardProps = {
  id: string;
  front: string;
  back?: string;
  source?: string;
  notes?: string;
  aiNotes?: Array<{ ts: number; note: string | string[]; type: AINotesTypes }>;
  createdTimeISO: string;
  deletedTimeISO?: string | null;
  /**
   * Per-card timestamp for LWW conflict resolution during sync merges.
   *
   * Legacy undifferentiated timestamp. Option F replaces this with the
   * paired `sharedUpdatedAtISO` / `personalUpdatedAtISO` below so the
   * merge routine can resolve shared-field vs personal-field edits
   * independently. New writes should populate all three for the
   * transition; old readers fall back to `updatedAtISO`.
   */
  updatedAtISO?: string;
  /** LWW timestamp for shared fields (front/back/images/aiNotes/sortKey/…).
   *  Bumped only when a shared field changes. Used by the group-side
   *  sync path to avoid clobbering personal-field edits with a stale
   *  shared-field broadcast. */
  sharedUpdatedAtISO?: string;
  /** LWW timestamp for personal fields (studyState/actions/starred/tags/
   *  notes/chat). Bumped only when a personal field changes. Never leaves
   *  the user's UserDo. */
  personalUpdatedAtISO?: string;
  actions: Array<CardAction>;
  studyState: Array<CardStudyState>;
  chat?: SyncChatMessage[];
  images?: {
    front: { src: string; format: ImageFormat };
    back: { src: string; format: ImageFormat };
  };
  tags?: CardTag[];
  starred?: boolean;
  /** When this card was moved/merged into its deck, if that deck already had
   *  a progress-reset floor — history from before it joined is not reset.
   *  Personal scope. */
  joinedDeckAtISO?: string;
  /** Tombstones of undone reviews: the ISO of every study entry / good-bad
   *  action the user removed with Undo. The cards merge removes them from
   *  every copy and never brings them back (`card-history-join.ts`).
   *  Personal scope; only grows. */
  undoneISO?: string[];
  /** Fractional-indexing key for stable, sync-friendly card ordering. See fractional-indexing lib */
  sortKey?: string;
  /** For cards generated from a timed source (video/audio transcript):
   *  seconds into the source media where the material this card was made
   *  from starts / ends. Lets study surfaces jump straight to the moment.
   *  Shared scope — the mapping describes the card, not the studier. */
  sourceTimeSec?: number;
  sourceTimeEndSec?: number;
};

/**
 * Pure predicate: a card is considered deleted if it has a `deletedTimeISO`
 * OR any `delete` action in its action history.
 *
 * Structurally typed so callers with a stricter shape (SyncCardProps, where
 * `actions` is required) AND callers with looser data (legacy Supabase rows
 * where `actions` may be missing) can both use it without coercion.
 */
export const isCardDeleted = (card: {
  deletedTimeISO?: string | null;
  actions?: Array<{ delete?: unknown }>;
}): boolean => {
  if (card.deletedTimeISO) return true;
  if (!card.actions) return false;
  for (const action of card.actions) {
    if (action.delete) return true;
  }
  return false;
};

// ---------------------------------------------------------------------------
// Deck properties
// ---------------------------------------------------------------------------

/**
 * A single study-group link for a deck. Owner-side and member-side decks
 * both carry this structure; `role` distinguishes the perspective.
 *
 * Multi-group sharing only stays on a single local deck when every link
 * is `editPermission: "owner-only"`. Otherwise the share flow auto-forks
 * the deck so edits in one group can't leak into another. See
 * `sync-engine/schema/link-rules.ts` once it lands.
 */
export type SyncLinkedGroup = {
  groupId: string;
  /** The deck's id within the study group (StudyGroupDo's DeckId). */
  sgDeckId: string;
  linkedAtISO: string;
  /** Mirror of the group's current edit permission — denormalised here so
   *  the client can apply the fork rule at share time without a separate
   *  group-settings lookup. Kept eventually-consistent via the same
   *  sg-deckProps broadcast that updates the group side. */
  editPermission: 'owner-only' | 'specific' | 'everyone';
  role: 'owner' | 'member';
};

/**
 * One source attached to a deck (PDF upload, transcription, AI prompt, manual,
 * APKG import, etc). Stored as an array on `SyncDeckMeta.sources` so a deck
 * can carry multiple sources in the future; today exactly one entry is
 * written by the lazy backfill in `useGetSource`.
 *
 * NOTE: `pdfInfo.extraction` is intentionally NOT included here. The heavy
 * `text_extraction_result` blob stays in the Supabase `text-extraction` table
 * and is fetched on demand by `filePath` via `/api/get-text-extraction`.
 * Only the path-to-source overview lives in sync, which is what lets the
 * client skip the bootstrap fetch on subsequent deck opens.
 */
/**
 * How a source entered the deck, when `kind` alone is ambiguous — a
 * `transcription` is both an audio upload and a YouTube link, a `pdf` is an
 * uploaded document or a photo scan. Additive: older validators pass it
 * through untouched and keep reading `kind`; entries written before
 * multi-source landed simply lack it.
 */
export type DeckSourceOrigin =
  | 'upload'
  | 'photos'
  | 'audio'
  | 'youtube'
  | 'text'
  /** A chapter of a Flashka Library book (see `DeckSource.library`). */
  | 'library';

/**
 * `DeckSource.library`: the Library chapter a PDF source IS — a book, one of
 * its units, and the release it came from. The source stays a plain `pdf`
 * (the chapter's own PDF, uploaded like any document), so every page tool and
 * every older client keep working; a client that knows this field shows the
 * chapter as the Library reads it instead, live figures included.
 */
export type DeckSourceLibraryRef = {
  /** The book (`fisica-1`). */
  slug: string;
  /** The unit (`ch05`, `verifica-1`). */
  unit: string;
  /** The release (`1.1`) — the deck keeps reading this one. */
  version: string;
};

/**
 * `DeckSource.textLength` — a document's measured length (see
 * `generation/document-length.ts`, the one place that writes and reads it).
 */
export type DeckSourceTextLength = {
  /** The coverage denominator: where the deck's LAST paragraph starts. */
  totalChars: number;
  /** Paragraphs on the deck's pages. */
  paragraphCount: number;
  /**
   * The file and page selection it was measured over (`documentLengthKey`).
   * A length whose key no longer matches the entry is stale: unknown, never
   * a number about pages the deck no longer reads.
   */
  pagesKey: string;
};

/** `SyncDeckMeta.sourceTombstones`: removed `sourceId` → removedAtISO. */
export type DeckSourceTombstones = Record<string, string>;

/** `SyncDeckMeta.sourcePrimaryStamps`: `sourceId` → when it was last made the first document. */
export type DeckSourcePrimaryStamps = Record<string, string>;

export type DeckSource = {
  /** Discriminator. Values mirror the existing PostHog `source_type` tags
   *  and the app's `deck_source_v2.source_type` PK column. CLOSED enum on the
   *  wire (`entities.ts`) — adding a member breaks older clients' validation.
   *  New provenance goes in `origin`, never here. */
  kind: 'pdf' | 'transcription' | 'photo' | 'apkg' | 'gpt' | 'manual' | 'text';
  /** See {@link DeckSourceOrigin}. Optional and additive. */
  origin?: DeckSourceOrigin;
  /** The originating link of a `youtube`-origin source. Optional and additive. */
  link?: { url: string; title?: string };
  /** Stable id for the source. For the first (and currently only) source
   *  on a deck this equals the deckId; future multi-source attachments
   *  generate a fresh uuid per entry. */
  sourceId: string;
  /** Opaque legacy `source` string preserved verbatim — filename, 'GPT',
   *  'MANUAL', 'APKG', '' — so consumers reading `data.source` keep working
   *  without a separate lookup. NOT the display name once the user has
   *  renamed the source: that is `title`. */
  sourceLabel?: string;
  /**
   * The name the USER gave this source (⋯ → Rename). Optional and additive:
   * absent until renamed, and older clients pass it through untouched. Wins
   * over the filename / video title everywhere the source is named; the file
   * itself (`sourceLabel`, `pdfInfo.filePath`) is never touched by a rename.
   */
  title?: string;
  addedAtISO: string;
  /**
   * Where the source sits in the deck's MATERIALS ORDER — the order the tab
   * bar and the All Sources grid draw, hand-arranged by dragging a tab or a
   * tile. A fractional-indexing key (`deck/materials/reorder.ts`), so two
   * devices reordering at once merge without renumbering. Optional and
   * additive: absent until the deck is first reordered, and an entry without
   * one sorts AFTER every keyed entry, in array order. The ARRAY position is
   * untouched by a reorder: `sources[0]` stays the primary whatever the user
   * drags where — the primary is a role, not a place in the strip.
   */
  sortKey?: string;
  /**
   * The entry's own EDIT clock (a rename, an Undo that put it back). Optional
   * and additive: absent until first edited, when `addedAtISO` stands in.
   * Two rows that meet merge `sources` per entry on it (see
   * `deck-materials-merge.ts`), exactly as the notes/maps index does. It
   * decides which copy's CONTENT wins — never whether a removed entry comes
   * back (that is `restoredAtISO`).
   */
  updatedAtISO?: string;
  /**
   * When an Undo last put this entry back after a removal. ONLY Undo writes
   * it, and it is the only clock besides `addedAtISO` that can outrank the
   * entry's `sourceTombstones` removal — so a rename on a stale device, or a
   * device whose clock runs fast, can never revive a removed document.
   * Merges to the later of both copies. Optional and additive.
   */
  restoredAtISO?: string;
  /** The clock of `sortKey` alone — set by a reorder. Optional and additive. */
  movedAtISO?: string;
  /**
   * How long the document's text is, measured over the deck's page selection
   * — what lets a surface show "X% covered" for a document whose extraction
   * it has not downloaded. Optional and additive: absent until a device that
   * had the text in hand recorded it (`recordDocumentLength`). Derived data,
   * so it never stamps the entry's edit clock, and the merge keeps whichever
   * copy still matches the entry's file and pages (`deck-materials-merge`).
   */
  textLength?: DeckSourceTextLength;
  /** See {@link DeckSourceLibraryRef}. Optional and additive. */
  library?: DeckSourceLibraryRef;
  /** Present iff `kind` is a PDF-backed source. Mirrors the legacy
   *  `pdfInfo` shape MINUS `extraction`. */
  pdfInfo?: {
    filePath: string;
    transcriptFilePath?: string;
    pages?: number[];
    transcriptionId?: string;
    /** Source-video overview for decks created from a video (YouTube).
     *  Rides inside `pdfInfo` because that is the source bag that already
     *  flows client → `ai-decks.additional_properties` → synced source.
     *  Older decks derive the same shape from the transcription record's
     *  `transcript_json.sourceUrl` (see `deriveVideoFromTranscript`). */
    video?: {
      provider: 'youtube';
      videoId: string;
      url: string;
      title?: string;
      durationSec?: number;
    };
  };
};

/**
 * A deck's NOTES and MIND MAPS — the index, not the content.
 *
 * A deck can hold several of each. Their content lives where it always has
 * (a note in its own Y.Doc keyed by `noteId`, a map in the `deckMindmaps`
 * record), and neither of those has anywhere to put a name or a provenance.
 * This is that place: which items exist, what each is called, and which
 * source it was generated from.
 *
 * It is what makes them exist ACROSS DEVICES at all. A note's Y.Doc is only
 * reachable if you know its id, so a note made on the phone is invisible on
 * the web until an id lands here. Kept on the deck meta, beside `sources`,
 * because that is the row every client already reads to know what a deck is
 * made of.
 *
 * Shared scope, like `sources`: every member of a linked deck sees the same
 * materials.
 *
 * MERGED PER ENTRY, not as one field. The deck meta row is last-write-wins,
 * but this index is unioned by id whenever two rows meet (server push, the
 * client's reconnect diff — `framework/deck-materials-merge.ts`), so a note
 * made on the phone while it was offline survives a rename made on the web
 * in the meantime. Two consequences for every entry:
 *   - `updatedAtISO` is the entry's OWN clock — every edit of a name or a
 *     provenance stamps it, and the newer entry wins per id. The order key
 *     has a clock of its own (`movedAtISO`), so a drag never outranks a
 *     rename of the same entry, nor a rename a drag.
 *   - a removal is a TOMBSTONE (`deletedAtISO`), never a filter: a stale
 *     copy that still lists the entry must not bring it back. Readers go
 *     through `deckNoteItems` / `deckMindmapItems`, which hide tombstones.
 */
export type DeckNoteItem = {
  /** Addresses the note's Y.Doc — `note\x01{deckId}\x01{noteId}`. */
  noteId: string;
  /** The name, auto-numbered at creation and renameable after. Absent on a
   *  legacy entry, which reads as its positional fallback. */
  title?: string;
  addedAtISO: string;
  /** The source this was generated FROM, when it was generated rather than
   *  written. A note is a deck-level item, not a child of a source — this
   *  records where it came from without nesting it. */
  fromSourceId?: string;
  /** Materials-order key — see {@link DeckSource.sortKey}. */
  sortKey?: string;
  /** `sortKey`'s OWN clock — when the entry was last placed. Kept apart from
   *  `updatedAtISO` so a drag here and a rename of the same entry elsewhere
   *  both land: the merge takes the name from the newer edit and the place
   *  from the newer drag. Absent on an entry never placed. */
  movedAtISO?: string;
  /** Per-entry LWW clock (name, provenance, removal); `addedAtISO` stands in
   *  until the first edit. A drag does NOT stamp it — see `movedAtISO`. */
  updatedAtISO?: string;
  /** Tombstone. Set when the user removed the item; sticky across merges. */
  deletedAtISO?: string;
};

export type DeckMindmapItem = {
  /** Addresses the map's `deckMindmaps` record — see `mindmapRecordKey`. */
  mindmapId: string;
  title?: string;
  addedAtISO: string;
  /** See {@link DeckNoteItem.fromSourceId}. */
  fromSourceId?: string;
  /** Materials-order key — see {@link DeckSource.sortKey}. */
  sortKey?: string;
  /** See {@link DeckNoteItem.movedAtISO}. */
  movedAtISO?: string;
  /** See {@link DeckNoteItem.updatedAtISO}. */
  updatedAtISO?: string;
  /** See {@link DeckNoteItem.deletedAtISO}. */
  deletedAtISO?: string;
};

export type DeckMaterialsIndex = {
  notes?: DeckNoteItem[];
  mindmaps?: DeckMindmapItem[];
};

/**
 * Per-deck exam study plan (the deck-shell Study tab's planner). The CLIENT
 * owns the full shape (`study-plan/exam-plan.ts` `ExamPlan`, shared by both
 * platforms; `study-plan/synced-plan.ts` is the binding that reads this field);
 * sync treats it as a mostly-opaque JSON blob and only pins the addressing
 * essentials — `version` (the client discards formats it doesn't know) and
 * `config.examDateISO` (the one field server features could ever need).
 * Personal scope: a study plan is the user's own pacing and never fans out
 * to study-group members (see DECK_FIELD_SCOPES).
 */
export type SyncExamPlan = {
  version: number;
  config: { examDateISO: string } & Record<string, unknown>;
} & Record<string, unknown>;

/** See {@link SyncDeckMeta.sgMerged}. */
export type SyncSgMerged = {
  intoDeckId: string;
  groupId: string;
  sgDeckId: string;
  deckPaths: DeckPath[];
  linkedGroups: SyncLinkedGroup[];
  atISO: string;
};

export type SyncDeckMeta = {
  id: DeckId;
  deckPaths: DeckPath[];
  displayName?: string;
  imageBackground?: string;
  folder?: string;
  color?: string;
  thumbnailText?: string;
  languageSettings?: { front: string | undefined; back: string | undefined };
  deletedTimeISO?: string;
  /** Set when the user explicitly archives the deck via the UI. Distinct
   *  from auto-archive (which is derived from the `deckactivity\x01{deckId}`
   *  inactivity timestamp). Manually-archived decks are hidden from library +
   *  sidebar and only visible in Settings → Archive. Cleared on unarchive. */
  manualArchivedAtISO?: string | null;
  /** The user opted this deck OUT of the "To-do" chip and every To-do counter
   *  (dashboard tiles, folder aggregates, the open-folder header pill, the
   *  To-do widget). Per-deck and self-contained: a folder has no flag of its
   *  own, its state is DERIVED from these (`dashboard/todo/exclusion.ts`), and
   *  a folder's bulk toggle just writes this on every deck inside. `false` /
   *  `undefined` = counted. Personal scope — a member's To-do preference never
   *  fans out through a group. */
  excludedFromTodo?: boolean;
  /** Per-deck exam study plan (deck-shell Study tab). Nullable so a plan
   *  reset writes an explicit `null` on the wire (same pattern as
   *  `manualArchivedAtISO`): `undefined` = never synced, `null` = explicitly
   *  cleared on some device — both read as "no plan". This field is the ONLY
   *  persistence the plan has on either platform; clients must never seed it
   *  from device-local storage (see `study-plan/synced-plan.ts` — a seed
   *  racing the initial sync-diff clobbers the whole deck-meta row under
   *  whole-row LWW). Personal scope — see DECK_FIELD_SCOPES. */
  examPlan?: SyncExamPlan | null;
  /** Progress-reset floor ("Reset flashcards progress"): a card of this deck
   *  with study history older than it reads as new. Sticky — the server keeps
   *  the max of stored and pushed, so a push without it cannot lift a reset.
   *  Personal scope — see DECK_FIELD_SCOPES. */
  progressResetAtISO?: string;
  /** Set by the server on a twin copy of a group deck once its history was
   *  merged into the copy that stays (`intoDeckId`): the copy keeps every row
   *  but loses its link and its `__sg_` path, so no resolver picks it again.
   *  The original link and paths are kept here. Sticky — the server restores
   *  it over any later push. Personal scope — see DECK_FIELD_SCOPES. */
  sgMerged?: SyncSgMerged;
  addedTimeISO?: string;
  orderIndex?: number;
  /** Fractional-indexing key for stable, sync-friendly sidebar ordering. */
  sortKey?: string;
  /** Timestamp of the most recent card change in this deck (for sync). */
  updatedAtISO?: string;
  /** Total number of cards in this deck. Maintained on the meta so it can be
   *  shown even for archived (cold) decks whose cards aren't on the device. */
  cardCount?: number;

  // ── Option F link metadata ────────────────────────────────────────
  //
  // Every local deck carries these. Empty `linkedGroups` + absent
  // origin fields = purely personal deck. Presence of `linkedGroups`
  // means writes to shared card fields propagate to each group; the
  // scope classification in `sync-engine/schema/field-scopes.ts` decides
  // which fields replicate.

  /** Study groups this deck currently participates in. Empty array for
   *  purely personal decks. */
  linkedGroups?: SyncLinkedGroup[];
  /** For a member-side deck, the owner's deck id. Undefined for the
   *  owner's own copy (owner's origin is the deck itself). */
  originDeckId?: DeckId;
  /** For a member-side deck, the owner's userId. Undefined for the
   *  owner's own copy. */
  originUserId?: string;
  /** Lineage: set when this deck was created by forking another. Used
   *  by the UI for the "Forked from X" breadcrumb and by the sharing
   *  service to preserve history across auto-forks. */
  forkedFromDeckId?: DeckId;

  // ── Phase 2 split-canonical scope fields (additive) ───────────────
  //
  // Personal-scope additions — every member of a shared deck has their
  // own value for these, and they NEVER propagate via the group. The
  // legacy `folder` / `sortKey` / `orderIndex` / `updatedAtISO` fields
  // above stay personal-scope; the new fields are forward-looking
  // names that match the §5.2 split-canonical spec. After the
  // wipe-and-reseed at final cutover, the legacy names go away and
  // these become the canonical personal-scope fields.
  personalFolderId?: string;
  personalDeckSortKey?: string;
  personalUpdatedAtISO?: string;

  // Shared-scope additions — for a shared deck, these live canonically
  // in the StudyGroupDo. On a member's UserDo they're a snapshot
  // populated by the SG broadcast (and stripped by Phase 6 lazy strip
  // on read). For a personal-only deck they remain undefined.
  sgFolderId?: string;
  sgDeckSortKey?: string;
  editPermission?: 'owner-only' | 'specific' | 'everyone';
  editors?: string[];
  addedByUserId?: string;
  sharedAtISO?: string;

  // SGDeckProps-shaped wire fields. Declared optional on SyncDeckMeta
  // so the unified `DeckFieldScopeMap` can classify them — without
  // these on the map, the SG-side `projectEntity` drops them on every
  // `sg-deckProperties-push` (see field-scopes.ts comment block on
  // those keys).
  /** The deck's routing key on the SG side; mirrors the client's
   *  `__sg_{groupId}_{deckName}` mirror key. */
  deckName?: string;
  /** SG-side folder id this deck belongs to. Distinct from the
   *  legacy personal-scope `folder` field. */
  folderId?: string | null;
  /** Whether the source PDF is hidden from non-owner members. */
  hideSource?: boolean;
  /** Per-scope timestamp paired with `personalUpdatedAtISO`. Travels
   *  with shared-field broadcasts so cross-scope LWW stays unambiguous. */
  sharedUpdatedAtISO?: string;

  /** PDF / source overview cache. Array shape enables multi-source decks
   *  in the future; today the lazy backfill writes exactly one entry on
   *  first deck open. Once present, the client short-circuits the
   *  `/api/get-ai-deck-source` fetch entirely — only the heavy
   *  `text_extraction_result` is fetched on demand by `filePath`. */
  sources?: DeckSource[];
  /**
   * Set when the user removed the LAST source from the deck.
   *
   * `sources: []` is ambiguous on its own — it is also what a deck looks like
   * before the read-side backfill has ever run — and both the backfill and the
   * pane's render-time fallback treat "empty" as "not established yet" and put
   * the deck's original document back. So removing the only source of a
   * one-document deck undid itself: the document returned, while the cards it
   * had generated kept the unlinked refs the removal gave them. This marker is
   * what makes the empty state DELIBERATE and therefore respected.
   */
  sourcesClearedAtISO?: string;
  /**
   * Documents REMOVED from the deck: `sourceId` → when. `sources` is unioned
   * by id when two rows meet, so a removal has to be recorded, not just left
   * out — or a stale copy from another device brings the document back. An
   * entry whose own clock is newer than its removal (an Undo) survives.
   * Additive: older clients pass it through untouched.
   */
  sourceTombstones?: DeckSourceTombstones;
  /**
   * When each document was last MADE the deck's first one (`sources[0]`, the
   * document every card ref without a document id reads). Written only by a
   * write that moves index 0 and pins the outgoing document's records in the
   * same change (`sync-client/deck-sources.ts`); the row merge puts the listed
   * document with the latest stamp first, whichever copy is newer — so a
   * stale or older-build copy's order cannot move it. Additive: older clients
   * pass it through or drop it, and a dropped copy yields to the other side.
   * A row without any (written before it existed, or never moved) keeps the
   * row-winner order.
   */
  sourcePrimaryStamps?: DeckSourcePrimaryStamps;
  /** The deck's notes and mind maps. See {@link DeckMaterialsIndex}. */
  materials?: DeckMaterialsIndex;

  // ── Link-sharing (sync-driven deck share, web) ────────────────────
  //
  // DISTINCT from the study-group fields above (`sharedAtISO` /
  // `sharedUpdatedAtISO`), which track group membership. These three
  // back the public `/d/:code` link-share flow and are classified
  // `personal` in DECK_FIELD_SCOPES so they NEVER fan out to study-group
  // members. The owner's UserDo mirrors `shareCode` → `{userId, deckId}`
  // into the `SHARE_DECK_KV` namespace inside `afterHandled`.

  /** Opaque base58 link-share code. Presence + `linkSharedAtISO` ⇒ the
   *  deck is link-shared. Source of truth the recipient route gates on. */
  shareCode?: string;
  /** When the link-share was created (ISO). */
  linkSharedAtISO?: string;
  /** Per-share toggle choices, honored by `/internal/get-shared-deck`
   *  when assembling the recipient snapshot. */
  shareOptions?: DeckShareOptions;
  /**
   * RECIPIENT side of a `/d/:code` import: the user whose link produced this
   * copy, stamped at import time when that owner has a public social profile.
   * Personal-scope, so it never fans out to study-group members. It is what
   * lets the "follow the person who shared this with you" nudge render on ANY
   * device from synced deck meta alone — the import happened on one client,
   * the nudge may appear on another. Absent for decks the user made
   * themselves, and for links whose owner is not on the social layer.
   */
  sharedByUserId?: string;
};

/** Per-share toggle choices for a link-shared deck. Mirrors the existing
 *  share dialog (Attach PDF / Share AI Helps / Share Notes). */
export type DeckShareOptions = {
  /** Include the deck's `sources` (PDF/transcription overview) in the snapshot. */
  sharePdf?: boolean;
  /** Include per-card `aiNotes` in the snapshot. */
  shareAiHelps?: boolean;
  /** Include the deck's notes in the snapshot. */
  shareNotes?: boolean;
  /** Client-snapshot of the default note (ProseMirror JSON), captured at
   *  share time via `notes.loadJSON()`. Replayed on the recipient's import.
   *  Present only when `shareNotes` is set. Kept beside `notesSnapshots` so
   *  an older worker / recipient that only knows ONE note keeps getting it. */
  notesSnapshot?: JSONContent;
  /**
   * EVERY note of a multi-note deck, captured at share time (the default note
   * included, so a recipient that reads this list needs nothing else). A note
   * is a Y.Doc in the owner's UserDo that no other user can open, so its
   * content has to travel with the share options; the deck's `materials`
   * index (names, provenance) is read live by the snapshot builder and joined
   * on `noteId`. Additive: absent on shares minted before multi-note.
   */
  notesSnapshots?: DeckNoteSnapshot[];
  /**
   * Notes the share carries whose content was NOT captured onto this row:
   * the snapshots written here are held under a hard size budget
   * (`SHARE_NOTES_SNAPSHOT_BUDGET_BYTES`), because this row syncs whole and a
   * Durable Object value past ~2 MB can never be written again. The owner's
   * UserDo FREEZES these notes from its own note documents when it receives
   * this share (a non-synced copy beside the row), and serves that copy —
   * never the note as it is at open time. Additive: absent on shares minted
   * before the budget.
   */
  liveNoteIds?: string[];
  /**
   * A fingerprint of the `liveNoteIds` notes' content as the owner's client
   * saw it when sharing. Never read for content: it only makes a re-share
   * after editing one of those notes a CHANGED row, so the UserDo re-freezes
   * its copy (an unchanged row is not re-written). Present iff `liveNoteIds` is.
   */
  liveNotesRev?: string;
};

/** One note's content as captured for a link share. */
export type DeckNoteSnapshot = {
  noteId: string;
  content: JSONContent;
};

// SyncUserInfo is now defined via Zod in sync-schema.ts and re-exported above.

// SyncFolder is now defined via Zod in sync-schema.ts and re-exported above.

// ---------------------------------------------------------------------------
// Notes (TipTap JSONContent)
// ---------------------------------------------------------------------------

/**
 * TipTap JSONContent shape — defined inline to avoid importing `@tiptap/react`.
 * This matches the structure from `@tiptap/core`'s `JSONContent` type.
 */
export type JSONContent = {
  type?: string;
  attrs?: Record<string, unknown>;
  content?: JSONContent[];
  marks?: Array<{
    type: string;
    attrs?: Record<string, unknown>;
    [key: string]: unknown;
  }>;
  text?: string;
  [key: string]: unknown;
};

/**
 * One tiptap note's JSON content. Still exported because the
 * `legacyNotesStash` table and the DO-emitted `legacy-notes-bundle`
 * wire message serialise notes in this shape while in transit between
 * Supabase and the per-user Y.Doc. Once every migrated user has
 * drained their stash, this type can disappear along with the stash.
 */
export type SyncNoteEntry = {
  content: JSONContent;
  updatedAtISO: string;
};

// ---------------------------------------------------------------------------
// Quiz progress
// ---------------------------------------------------------------------------

export type SyncFlashcardSourceSnapshot = {
  type: 'flashcard';
  originalCardId: string;
  front: string;
  back: string;
  capturedAtISO: string;
};

export type SyncParagraphSourceSnapshot = {
  type: 'paragraph';
  content: string;
  pageNumber: number;
  characterOffsets: { start: number; end: number };
  deckName: string;
  /** The deck document the offsets index into; absent = the deck's primary. */
  sourceId?: string;
  capturedAtISO: string;
};

export type SyncNotesExcerptSourceSnapshot = {
  type: 'notesExcerpt';
  content: string;
  chapterTitle?: string;
  /** The note and block (its editor id) the excerpt was read from. */
  noteId?: string;
  blockId?: string;
  cardIds?: string[];
  /** Read only by app builds v3.19.0–v3.21.4, which key it as a document passage. */
  pageNumber?: number;
  characterOffsets?: { start: number; end: number };
  deckName?: string;
  capturedAtISO: string;
};

export type SyncQuizSourceSnapshot =
  | SyncFlashcardSourceSnapshot
  | SyncParagraphSourceSnapshot
  | SyncNotesExcerptSourceSnapshot;

export type SyncQuizSourcesMap = {
  [questionIndex: string]: SyncQuizSourceSnapshot[];
};

export type SyncQuizResponse = {
  correct: boolean;
  correctness?: 'success' | 'fail' | 'medium' | 'skipped';
  text: string;
  ms?: number;
};

export type SyncQuizEndReason =
  | 'completed'
  | 'submitted'
  | 'timeUp'
  | 'left'
  | 'genFailed';

export type SyncQuizReportedQuestion = {
  id: string;
  /**
   * Id of the question it came after: the nearest earlier one the attempt
   * still kept when this was reported; `null` when it came first. Reports put
   * back newest first, each after its anchor, give the order it was asked in
   * across any number of resumes (`questionOrder`).
   */
  after: string | null;
  kind?: string;
  sourceIds?: string[];
  response?: SyncQuizResponse;
  ms?: number;
  reasons: string[];
  other?: string;
};

export type SyncQuizAttemptTelemetry = {
  schemaV?: 1;
  requestedSize?: number;
  difficulty?: 0 | 1 | 2;
  firstQuestionMs?: number;
  batchErrors?: number;
  generationError?: 'outOfCredits' | 'generation';
  invalidDropped?: number;
  endReason?: SyncQuizEndReason;
  maxReachedId?: string;
  reportedDetail?: SyncQuizReportedQuestion[];
  retakeOf?: string;
  retakeSelection?: 'all' | 'wrong';
};

export const QUIZ_ATTEMPT_TELEMETRY_KEYS = [
  'schemaV',
  'requestedSize',
  'difficulty',
  'firstQuestionMs',
  'batchErrors',
  'generationError',
  'invalidDropped',
  'endReason',
  'maxReachedId',
  'reportedDetail',
  'retakeOf',
  'retakeSelection',
] as const satisfies readonly (keyof SyncQuizAttemptTelemetry)[];

export type SyncQuizProgressData = SyncQuizAttemptTelemetry & {
  ts: string; // ISO timestamp = attempt ID
  quizzes: string[]; // question texts
  responses: SyncQuizResponse[][]; // answers per question
  reportedQuestions?: string[];
  deckId: string;
  userId?: string;
  correctImmediately: boolean;
  quizMode: 'examSimulation' | 'quiz';
  quizTimerSeconds: number | null;
  quizName?: string;
  questionTypes?: string[];
  quizSourceType?: 'flashcards' | 'paragraphs' | 'notes';
  /** The document a `paragraphs` quiz reads, by source id. Absent: the deck's first. */
  quizSourceId?: string;
  /** The note a `notes` quiz reads, by id. */
  quizNoteId?: string;
  completedAt?: string; // ISO timestamp; absent ⇒ in-progress
  sources?: SyncQuizSourcesMap;
  // Resume support: cursor + randomized MCQ correct slots must persist so
  // a refreshed quiz lands on the same question and doesn't re-shuffle
  // already-given MCQ answers.
  currentQuestionIndex?: number;
  mcqCorrectIndexes?: number[];
  // Parsed twin of `quizzes[]` (same index; null = unparseable block) — see
  // QuizSessionAttempt.questions. Present from formatVersion 2 on.
  questions?: (StoredQuizQuestion | null)[];
  formatVersion?: 1 | 2;
};

export type SyncQuizProgressEntry = {
  deckId: DeckId;
  quizTs: string; // original quiz timestamp (key)
  attempts: SyncQuizProgressData[]; // array of attempts (retakes)
  updatedAtISO: string; // LWW timestamp
  isFolderQuiz?: boolean;
  userId?: string;
  deletedTimeISO?: string; // soft-delete marker
  // Share authorization: presence of `sharedAt` is the source of truth for
  // "this quiz is currently shared". LWW-merged via the same `...winner`
  // spread that carries every other field on the entry.
  sharedAt?: string;
  shareCode?: string;
  // Discriminates non-quiz entries that share this entity from regular
  // quizzes. Absent ⇒ a normal quiz. `'oral'` and `'caseStudy'` entries store
  // a DIFFERENT opaque attempt shape in `attempts` (an `OralExamAttemptRecord[]`
  // / `CaseStudyAttemptRecord[]`, not `SyncQuizProgressData[]`). Every quiz
  // consumer (web + mobile) MUST skip entries with a non-null `examKind` (use
  // {@link isNonQuizEntry}) so they never render as quiz rows or feed quiz
  // stats. The merge engine is agnostic to it (LWW `...winner`, sticky).
  examKind?: 'oral' | 'caseStudy';
  restartOf?: string;
};

/** Build the composite key for a quiz progress entry. */
export function quizKey(deckId: string, quizTs: string): string {
  return `${deckId}::${quizTs}`;
}

/**
 * A `quizProgress` entry that is NOT a plain quiz — it carries an `examKind`
 * discriminator (`'oral'`, `'caseStudy'`, or any future non-quiz kind). Quiz
 * lists, stats, history and resume flows MUST skip these so the shared
 * entity's non-quiz members never render as quiz rows or feed quiz stats.
 * Loosely typed (`examKind?: string | null`) so callers can pass entries typed
 * by a vendored/older `@flashka/shared` without casting.
 */
export function isNonQuizEntry(entry: { examKind?: string | null }): boolean {
  return entry.examKind != null;
}

// ---------------------------------------------------------------------------
// PDF annotations (notes, explanations, askKa)
// ---------------------------------------------------------------------------

export type SyncPdfAnnotation = {
  content: string;
  /**
   * Character offset range in PDF text, format: "m:START-END" — the deck's
   * primary document, implicitly. Pinned to that document by id
   * (`s:{sourceId}:m:START-END`, the card-ref grammar) when the deck's
   * documents move, so the anchor never lands in another document.
   */
  source: string;
  id: string;
  ts: string;
  multiPagePosition?: { x: number; y: number; offset: number };
};

export type SyncAskKaAnnotation = SyncPdfAnnotation & {
  chat: SyncChatMessage[];
};

export type SyncPdfAnnotations = {
  notes: SyncPdfAnnotation[];
  explanations: SyncPdfAnnotation[];
  askKa: SyncAskKaAnnotation[];
};

/**
 * Per-deck annotations entry for the `annotations` record entity.
 * One entry per deck, keyed by `deckName`.
 */
export type SyncAnnotationsRecordEntry = {
  deckName: string;
  annotations: SyncPdfAnnotations;
  updatedAtISO: string;
};

// ---------------------------------------------------------------------------
// Deck mindmaps (the study-set page "Mindmaps" tab)
// ---------------------------------------------------------------------------

/** One mindmap node: short label, unique id, parent link, optional PDF source. */
export type SyncMindmapNode = {
  id: string;
  label: string;
  parentId?: string;
  /** Where in the deck's PDF the node came from (`m:start-end` offsets). */
  source?: { range: string; pageNumber: number; deckName: string };
};

/**
 * Per-deck mindmap entry for the `deckMindmaps` record entity — ONE mindmap
 * per deck (like flashcards' single deck record, not like the Yjs notes doc),
 * keyed by `deckName`. `mindmap` is the logical tree the model authored (plus
 * user structure edits); `layout` is the user-arranged rendered state
 * (positions + labels) persisted on "Save changes", absent until then. Whole-
 * entry LWW by `updatedAtISO` is enough: a mindmap is edited by one user.
 * Shapes are owned by the mindmap UI (`flashka-web/src/components/chat/
 * mindmap/mindmap-types.ts`) and stay OPAQUE to inbound validation
 * (`z.unknown()` inner shapes, per the policy in entities.ts).
 */
export type SyncDeckMindmapEntry = {
  deckName: string;
  mindmap: { title?: string; nodes: SyncMindmapNode[] };
  layout?: {
    nodes: Array<{ id: string; label: string; x: number; y: number }>;
    edges: Array<{ id: string; source: string; target: string }>;
  };
  /**
   * The infinite-canvas GRAPH (`MindmapGraph`, version 2 — see
   * `shared/src/mindmap/mindmap-graph.ts`). When present it is the map;
   * `mindmap` + `layout` are then a projection an older client can still draw.
   * Absent = a v1 map, upgraded to a graph when read. Opaque here.
   */
  graph?: unknown;
  /**
   * `mindmap` + `layout` were saved AFTER `graph`, by a client that knows only
   * them: the server kept the graph rather than drop it, and a canvas client
   * folds the newer tree back into it when it reads (`reconcileGraph`).
   */
  graphStale?: true;
  updatedAtISO: string;
};

// ---------------------------------------------------------------------------
// Professor Ka chat conversations
// ---------------------------------------------------------------------------

/**
 * A "Professor Ka" agentic-chat conversation, stored in the
 * `chatConversations` record entity (one entry per conversation, keyed by
 * `id`).
 *
 * `messages` holds the AI SDK message list (`UIMessage[]`) verbatim — it is
 * OPAQUE to the sync layer (`z.array(z.unknown())` in the entity schema, per
 * the inbound-validation policy in entities.ts). Its shape is owned by the
 * chat UI, so it can carry tool invocations / parts without a wire change.
 * As with `SyncChatMessage`, any `Date` fields serialize to strings through
 * IndexedDB / the sync engine and back; the AI SDK handles the conversion.
 */
export type SyncChatConversationEntry = {
  /** Conversation id (uuid). Primary key. */
  id: string;
  /** Display title — derived from the first user message ('' until then). */
  title: string;
  /** AI SDK messages, stored verbatim. Opaque to the sync layer. */
  messages: unknown[];
  /** Deck ids tagged / referenced across this conversation (context + recency). */
  deckIds?: string[];
  createdTimeISO: string;
  /** LWW timestamp. */
  updatedAtISO: string;
  /** Soft-delete marker. */
  deletedTimeISO?: string;
};
